import {
  ConsultantQualityCheckConsultantDataFragment,
  ConsultantQualityCheckDataFragment,
  ConsultantQuality_ConsultantNewFragment
} from "@bonliva-crm/graphql";
import { DateTime } from "luxon";
import { SendViaOneflowTransitionParams } from "~/consultant-quality/health-declaration/send-via-oneflow.transition-handler";
import locale from "@bonliva-crm/locale";
import { ManuallyHdStatusTransitionParams } from "~/consultant-quality/health-declaration/manually-set-hd-status.transition-handler";

export function isDisabledByStatus(data: ConsultantQualityCheckDataFragment) {
  return !!data.Status && ["RequestSent"].includes(data.Status.Name);
}

export function isSendAllowed(
  consultantData:
    | ConsultantQuality_ConsultantNewFragment
    | ConsultantQualityCheckConsultantDataFragment
) {
  return (
    consultantData.SocialSecurityNo &&
    consultantData.ResponsiblePerson?.PersonId &&
    consultantData.EmailValid &&
    (consultantData.PhoneValid || consultantData.CellPhoneValid)
  );
}

export function getLastFullHDStatus(
  data: ConsultantQualityCheckDataFragment
): string | null {
  if (data.Status) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    if (data.Status.Name === data.Status?.params?.lastStatusFullHD) {
      return null;
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    return locale.searchQualityCheckStatus[data.Status?.params?.lastStatusFullHD];
  }
  return null;
}

export function isNowHalfYearAfterSetOn(data: ConsultantQualityCheckDataFragment) {
  if (!data.Status) {
    return false;
  }
  const setOn = DateTime.fromISO(data.StatusSetOn.toString());
  const halfYear = setOn.plus({ months: 6 });
  const now = DateTime.utc();
  return now > halfYear;
}

export function mrsaDisabled(
  data: ConsultantQualityCheckDataFragment,
  isNowHalfYearAfterSetOn: boolean
) {
  if (!data.Status) {
    return true;
  }
  // if expired, we should not send the MLRA template
  if (data.HasExpired) {
    return true;
  }

  if (isNowHalfYearAfterSetOn) {
    return !(
      !!data.Status && ["Approved", "ApprovedWithComment"].includes(data.Status.Name)
    );
  }
  return true;
}

export function fullHDDisabled(
  data: ConsultantQualityCheckDataFragment,
  isNowHalfYearAfterSetOn: boolean
) {
  return !(!data.Status || isNowHalfYearAfterSetOn);
}

export function getTransitionParams(
  data: ConsultantQualityCheckDataFragment,
  forceFullHD: boolean
): SendViaOneflowTransitionParams | null {
  if (!!data.Status && ["Approved", "ApprovedWithComment"].includes(data.Status.Name)) {
    // if expired, we should not send the MLRA template
    if (data.HasExpired) {
      return null;
    }

    const params: any = {
      lastComment: data.Status.Comment,
      lastStatusFullHD: data.Status.Name
    };

    // if added signedDateFullHD we send the MLRA template if not FullHD
    if (!forceFullHD) {
      params.signedDateFullHD = data.StatusSetOn;
    }

    // if approved or approved with comment, we need to send the signed date and last comment to send MLRA template
    return params;
  }

  return null;
}

export function getQIParamsIfITwasSet(
  data: ConsultantQualityCheckDataFragment,
  formValue: { setOn: string; comment: string }
): ManuallyHdStatusTransitionParams | null {
  if (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    data.Status?.params
  ) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    return { ...data.Status.params, formValue };
  }

  if (
    isPartiallyExpired("Hälsodeklaration", data.Status, data.HasExpired, data.StatusSetOn)
  ) {
    return {
      signedDateFullHD: formValue.setOn?.toString(),
      lastComment: data.Status.Comment,
      lastStatusFullHD: data.Status.Name,
      signedDateMRSAOnlyHD: new Date().toISOString(),
      formValue
    };
  }

  if (formValue) {
    return { formValue };
  }
  return null;
}

export function isPartiallyExpired(
  name: string,
  status: ConsultantQualityCheckDataFragment["Status"],
  hasExpired: boolean,
  statusSetOn: Date
): boolean {
  if (name !== "Hälsodeklaration") {
    return false;
  }
  if (!!status && ["Approved", "ApprovedWithComment"].includes(status.Name)) {
    if (hasExpired) {
      // full expired
      return false;
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const mrsaSignedDate = status?.params?.signedDateMRSAOnlyHD;
    if (mrsaSignedDate) {
      return false;
    }
    const _setOn = DateTime.fromISO(statusSetOn.toString());
    const halfYear = _setOn.plus({ months: 6 });
    const now = DateTime.utc();
    if (now > halfYear) {
      return true;
    }
  }
}

export function getRequestSentTitle(
  title: string,
  data: ConsultantQualityCheckDataFragment
) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (data.Status.Name === "RequestSent" && data.Status?.params?.signedDateFullHD) {
    return `${title} MRSA`;
  } else {
    return title;
  }
}
